import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { isAfter, isEqual, formatISO, parseISO } from 'date-fns';
import Button from '@ui-elements/Button';
import Checkbox from '@ui-elements/Checkbox';
import Typography from '@mui/material/Typography';
import SearchIcon from '@mui/icons-material/Search';
import DatePicker from '@ui-parts/DatePicker';
import TimePicker from '@ui-parts/TimePicker';
import { emptyValueFilter } from '@functional';
import InputValidationErrorBox from '@ui-parts/InputValidationErrorBox';
import { OutlinedInput } from '@mui/material';
import { PALETTE, FONT_SIZES, FONT_WEIGHTS, BASE_FONT_COLOR } from '~/assets/styles/base';
import { callThunk, fetchSurveysSelectByAdmin } from '~/api';
import { JUDGEMENT_STATUS } from '~/common/constants';
import {
  SURVEY_STATUSES_NAMES,
  ANTISOCIAL_CHECK_NAME,
  ANTISOCIAL_CHECK_APPROVAL_NAME,
  CREDIT_CHECK_NAME,
  SESSION_STORAGES,
} from '~/constants';

const Wrapper = styled.div`
  margin: 0 0 28px;
  background-color: ${PALETTE.white};
  border: 1px solid ${PALETTE.border.table};
  text-align: left;
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 23px;
  height: 41px;
  line-height: 41px;
  background-color: ${PALETTE.blue.main};
`;

const Body = styled.div`
  padding: 9px 23px 30px;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  font-size: ${FONT_SIZES.title5};
  background: ${PALETTE.bg.main};
  margin-top: 8px;
  padding: 0 8px;
`;

const AntiSocialCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${FONT_SIZES.title5};
  margin-top: 17px;
`;

const CheckboxesTitle = styled.div`
  font-size: ${FONT_SIZES.title5};
  margin-bottom: 8px;
`;

const DateWrapper = styled.div`
  margin-top: 16px;
`;

const DateInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${PALETTE.bg.main};
  padding: 11px 11px 11px 0;
  margin-top: 8px;
  p {
    width: 0;
    margin-right: 13px;
  }
`;

const CheckboxesInner = styled.div`
  display: flex;
  text-wrap: nowrap;
  align-items: center;
  padding: 0 25px 0 11px;
  background: ${PALETTE.bg.main};
`;

const StyledCheckbox = styled(Checkbox)`
  label {
    margin-right: 0;
  }
  span {
    font-size: ${FONT_SIZES.body2};
  }
  .MuiCheckbox-root:after {
    content: '';
    background-color: ${PALETTE.white};
    left: 14px;
    top: 14px;
    height: 20px;
    width: 20px;
    position: absolute;
    z-index: -1;
  }
`;

const StyledButton = styled(Button)`
  display: flex;
  width: 100px;
  height: 32px;
  margin: 13px auto 0;
  font-size: ${FONT_SIZES.body4};
  &.MuiButton-contained {
    min-height: auto;
  }
`;

const ClearButton = styled(Button)`
  display: block;
  width: 128px;
  height: 25px;
  margin: 0;
  line-height: 1;
  font-size: ${FONT_SIZES.title5};
  background-color: ${PALETTE.blue.light};
  border: 1px solid #a2b2be52;
  border-radius: 13px;
  transition: opacity 0.4s;
  &.MuiButton-outlined {
    color: ${BASE_FONT_COLOR};
    font-weight: ${FONT_WEIGHTS.base};
    min-height: auto;
  }
  &:hover {
    background-color: ${PALETTE.blue.light};
    opacity: 0.7;
  }
`;

const StyledSearchIcon = styled(SearchIcon)`
  font-size: 1.8rem;
  margin-right: 4px;
  font-weight: ${FONT_WEIGHTS.size2};
`;

const StartArea = styled.div`
  display: flex;
  align-items: center;
`;

const EndArea = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: -24px;
    width: 28px;
    height: 2px;
    background-color: ${PALETTE.gray[500]};
  }
`;

const TimePickerWrapper = styled.div`
  div {
    height: 40px;
  }
`;

const SurveyOwnerInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: ${FONT_SIZES.title5};
  margin-top: 8px;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 50px 8px 11px;
  background: ${PALETTE.bg.main};
  margin-top: 8px;
`;

const StyleOutlinedInput = styled(OutlinedInput)`
  &.MuiOutlinedInput-root {
    background: ${PALETTE.white};
  }
  &.MuiOutlinedInput-root .MuiOutlinedInput-input {
    color: ${PALETTE.text.main};
    font-size: ${FONT_SIZES.body2};
    padding: 5px 0 5px 11px;
    background: ${PALETTE.white};
  }
  input::placeholder {
    font-size: ${FONT_SIZES.body2};
  }
`;

const initialError = {
  invalid: false,
  message: '',
};

function SurveySearchBox({ initState = null }) {
  const [userName, setUserName] = useState(initState?.userName || '');
  const [userId, setUserId] = useState(initState?.userId || '');
  const [surveyId, setSurveyId] = useState(initState?.surveyId || '');

  const [surveyStatusIds, setSurveyStatusIds] = useState(initState?.surveyStatusIds || []);
  const [antisocialCheckStatus, setAntisocialCheckStatus] = useState(
    initState?.antisocialCheckStatus || []
  );
  const [antisocialCheckApprovalStatus, setAntisocialCheckApprovalStatus] = useState(
    initState?.antisocialCheckApprovalStatus || []
  );
  const [creditCheckStatus, setCreditCheckStatus] = useState(initState?.creditCheckStatus || []);
  const [startDateStr, setStartDateStr] = useState(initState?.startDateStr || null);
  const [startTimeStr, setStartTimeStr] = useState(initState?.startTimeStr || '00:00');
  const [endDateStr, setEndDateStr] = useState(initState?.endDateStr || null);
  const [endTimeStr, setEndTimeStr] = useState(initState?.endTimeStr || '00:00');
  const [errors, setErrors] = useState(initialError);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // 日付startとendを比較
  const validateDate = () => {
    setErrors(initialError);
    if (startDateStr == null || endDateStr == null) return true; // 比較対象がなければ早期リターン

    if (
      isAfter(
        parseISO(`${startDateStr} ${startTimeStr}`),
        parseISO(`${endDateStr} ${endTimeStr}`)
      ) ||
      isEqual(parseISO(`${startDateStr} ${startTimeStr}`), parseISO(`${endDateStr} ${endTimeStr}`))
    ) {
      setErrors({
        invalid: true,
        message: t('validation.notAvailableSearchEndDateTime'),
      });
      return false;
    }
    return true;
  };

  const handleOnSurveyStatusChange = (e) => {
    let newIds = [...surveyStatusIds];
    if (e.target.checked) {
      newIds.push(e.target.value);
    } else {
      newIds = newIds.filter((value) => value !== e.target.value);
    }
    setSurveyStatusIds(newIds.sort());
  };

  const handleOnAntisocialCheckChange = (e) => {
    let newIds = [...antisocialCheckStatus];
    if (e.target.checked) {
      newIds.push(e.target.value);
    } else {
      newIds = newIds.filter((value) => value !== e.target.value);
    }
    setAntisocialCheckStatus(newIds.sort());
  };

  const handleOnAntisocialCheckApprovalChange = (e) => {
    let newIds = [...antisocialCheckApprovalStatus];
    if (e.target.checked) {
      newIds.push(e.target.value);
    } else {
      newIds = newIds.filter((value) => value !== e.target.value);
    }
    setAntisocialCheckApprovalStatus(newIds.sort());
  };

  const handleOnCreditCheckChange = (e) => {
    let newIds = [...creditCheckStatus];
    if (e.target.checked) {
      newIds.push(e.target.value);
    } else {
      newIds = newIds.filter((value) => value !== e.target.value);
    }
    setCreditCheckStatus(newIds.sort());
  };

  const handleOnClickSearch = () => {
    if (validateDate()) {
      const deliveryDateStart = startDateStr
        ? formatISO(new Date(`${startDateStr} ${startTimeStr}`))
        : null;
      const deliveryDateEnd = endDateStr
        ? formatISO(new Date(`${endDateStr} ${endTimeStr}`))
        : null;

      const params = {
        userName,
        userId,
        surveyId,
        surveyStatusIds: surveyStatusIds.join(','),
        antisocialCheckStatus: antisocialCheckStatus.join(','),
        antisocialCheckApprovalStatus: antisocialCheckApprovalStatus.join(','),
        creditCheckStatus: creditCheckStatus.join(','),
        deliveryDateStart,
        deliveryDateEnd,
      };

      dispatch(
        callThunk({
          action: fetchSurveysSelectByAdmin,
          payload: emptyValueFilter(params),
        })
      );

      // 条件をストレージに保存
      sessionStorage.setItem(
        SESSION_STORAGES.SURVEY_SEARCH_BOX_CONDITIONS,
        JSON.stringify({
          userName,
          userId,
          surveyId,
          surveyStatusIds,
          antisocialCheckStatus,
          antisocialCheckApprovalStatus,
          creditCheckStatus,
          startDateStr,
          startTimeStr,
          endDateStr,
          endTimeStr,
        })
      );
    }
  };

  const handleOnClickClear = () => {
    setUserName('');
    setUserId('');
    setSurveyId('');
    setSurveyStatusIds([]);
    setAntisocialCheckStatus([]);
    setAntisocialCheckApprovalStatus([]);
    setCreditCheckStatus([]);
    setStartDateStr(null);
    setEndDateStr(null);
    setStartTimeStr('00:00');
    setEndTimeStr('00:00');
    sessionStorage.removeItem(SESSION_STORAGES.SURVEY_SEARCH_BOX_CONDITIONS);
  };

  return (
    <Wrapper>
      <Head>
        <Typography variant="title3" fontWeight="bold">
          {t('myPage.surveySearchBox.title')}
        </Typography>
        <ClearButton
          colorType="gray"
          variant="outlined"
          onClick={handleOnClickClear}
          data-testid="uiParts-SurveySearchBox-clear-conditions"
        >
          {t('myPage.surveySearchBox.clear')}
        </ClearButton>
      </Head>
      <Body>
        <SurveyOwnerInfoWrapper>
          <div>
            <Typography variant="title5">{t('myPage.surveySearchBox.surveyId')}</Typography>
            <InputWrapper>
              <StyleOutlinedInput
                inputProps={{ 'data-testid': 'uiParts-SurveySearchBox-surveyId' }}
                placeholder={t('myPage.surveySearchBox.surveyId')}
                value={surveyId}
                onChange={(e) => {
                  setSurveyId(String(Number(e.target.value.replace(/[^0-9]/g, ''))));
                }}
                onBlur={(e) => {
                  if (e.target.value === '0') {
                    setSurveyId('');
                  } else {
                    setSurveyId(e.target.value);
                  }
                }}
              />
            </InputWrapper>
          </div>

          <div>
            <Typography variant="title5">{t('myPage.surveySearchBox.userName')}</Typography>
            <InputWrapper>
              <StyleOutlinedInput
                inputProps={{ 'data-testid': 'uiParts-SurveySearchBox-userName' }}
                placeholder={t('myPage.surveySearchBox.userName')}
                value={userName}
                onChange={(e) => {
                  setUserName(e.target.value);
                }}
                onBlur={(e) => {
                  setUserName(e.target.value);
                }}
              />
            </InputWrapper>
          </div>

          <div>
            <Typography variant="title5">{t('myPage.surveySearchBox.userId')}</Typography>
            <InputWrapper>
              <StyleOutlinedInput
                inputProps={{ 'data-testid': 'uiParts-SurveySearchBox-userId' }}
                placeholder={t('myPage.surveySearchBox.userId')}
                value={userId}
                onChange={(e) => {
                  setUserId(String(Number(e.target.value.replace(/[^0-9]/g, ''))));
                }}
                onBlur={(e) => {
                  if (e.target.value === '0') {
                    setUserId('');
                  } else {
                    setUserId(e.target.value);
                  }
                }}
              />
            </InputWrapper>
          </div>
        </SurveyOwnerInfoWrapper>

        <Typography variant="title5">{t('myPage.surveySearchBox.status')}</Typography>
        <CheckboxWrapper>
          {Object.keys(SURVEY_STATUSES_NAMES).map((key) => {
            return (
              <StyledCheckbox
                dataTestId={`uiParts-SurveySearchBox-surveyStatus-${key}`}
                key={key}
                value={key}
                label={SURVEY_STATUSES_NAMES[key]}
                handleChange={handleOnSurveyStatusChange}
                fontSize={30}
                checked={surveyStatusIds.includes(key)}
              />
            );
          })}
        </CheckboxWrapper>
        <AntiSocialCheckboxWrapper>
          <div>
            <CheckboxesTitle>{t('myPage.surveySearchBox.antiSocialCheckTitle')}</CheckboxesTitle>
            <CheckboxesInner>
              {Object.keys(ANTISOCIAL_CHECK_NAME).map((key) => {
                return (
                  <StyledCheckbox
                    dataTestId={`uiParts-SurveySearchBox-antisocialCheck-${key}`}
                    key={key}
                    value={key}
                    label={ANTISOCIAL_CHECK_NAME[key]}
                    handleChange={handleOnAntisocialCheckChange}
                    fontSize={30}
                    checked={antisocialCheckStatus.includes(key)}
                  />
                );
              })}
            </CheckboxesInner>
          </div>
          <div>
            <CheckboxesTitle>
              {t('myPage.surveySearchBox.antisocialCheckApprovalTitle')}
            </CheckboxesTitle>
            <CheckboxesInner>
              {Object.keys(ANTISOCIAL_CHECK_APPROVAL_NAME).map((key) => {
                return (
                  Number(key) !== JUDGEMENT_STATUS.UNNEEDED && (
                    <StyledCheckbox
                      dataTestId={`uiParts-SurveySearchBox-antisocialCheckApproval-${key}`}
                      key={key}
                      value={key}
                      label={ANTISOCIAL_CHECK_APPROVAL_NAME[key]}
                      handleChange={handleOnAntisocialCheckApprovalChange}
                      fontSize={30}
                      checked={antisocialCheckApprovalStatus.includes(key)}
                    />
                  )
                );
              })}
            </CheckboxesInner>
          </div>
          <div>
            <CheckboxesTitle>{t('myPage.surveySearchBox.creditCheckTitle')}</CheckboxesTitle>
            <CheckboxesInner>
              {Object.keys(CREDIT_CHECK_NAME).map((key) => {
                return (
                  <StyledCheckbox
                    dataTestId={`uiParts-SurveySearchBox-creditCheck-${key}`}
                    key={key}
                    value={key}
                    label={CREDIT_CHECK_NAME[key]}
                    handleChange={handleOnCreditCheckChange}
                    fontSize={30}
                    checked={creditCheckStatus.includes(key)}
                  />
                );
              })}
            </CheckboxesInner>
          </div>
        </AntiSocialCheckboxWrapper>
        <DateWrapper>
          <Typography variant="title5">{t('myPage.surveySearchBox.deliveryDate')}</Typography>
          <DateInner>
            <StartArea>
              <div>
                <DatePicker
                  label=""
                  date={startDateStr}
                  handleChange={(date) => setStartDateStr(date)}
                  handleClear={() => setStartDateStr(null)}
                  width="144px"
                  dataTestId="uiParts-startDatePicker"
                />
              </div>
              <TimePickerWrapper>
                <TimePicker
                  label=""
                  handleChange={
                    /* istanbul ignore next プラグインへのEvent発火が難しい為、除外 */
                    (_, timeStr) => setStartTimeStr(timeStr)
                  }
                  width="200px"
                  dataTestId="uiParts-startTimePicker"
                  value={startTimeStr}
                />
              </TimePickerWrapper>
            </StartArea>
            <EndArea>
              <div>
                <DatePicker
                  label=""
                  date={endDateStr}
                  handleChange={(date) => setEndDateStr(date)}
                  handleClear={() => setEndDateStr(null)}
                  width="144px"
                  dataTestId="uiParts-endDatePicker"
                />
              </div>
              <TimePickerWrapper>
                <TimePicker
                  label=""
                  handleChange={
                    /* istanbul ignore next defaultプラグインへのEvent発火が難しい為、除外 */
                    (_, timeStr) => setEndTimeStr(timeStr)
                  }
                  width="200px"
                  dataTestId="uiParts-endTimePicker"
                  value={endTimeStr}
                />
              </TimePickerWrapper>
            </EndArea>
          </DateInner>
          {errors.invalid && (
            <InputValidationErrorBox
              message={errors.message}
              dataTestId="DeliveryDateModal-TimePicker"
              position="right"
              width="429px"
            />
          )}
        </DateWrapper>

        <StyledButton color="primary" variant="contained" onClick={handleOnClickSearch}>
          <StyledSearchIcon />
          {t('myPage.surveySearchBox.search')}
        </StyledButton>
      </Body>
    </Wrapper>
  );
}

SurveySearchBox.propTypes = {
  initState: PropTypes.shape({
    userName: PropTypes.string,
    userId: PropTypes.string,
    surveyId: PropTypes.string,
    surveyStatusIds: PropTypes.arrayOf(PropTypes.string),
    antisocialCheckStatus: PropTypes.arrayOf(PropTypes.string),
    antisocialCheckApprovalStatus: PropTypes.arrayOf(PropTypes.string),
    creditCheckStatus: PropTypes.arrayOf(PropTypes.string),
    startDateStr: PropTypes.string,
    startTimeStr: PropTypes.string,
    endDateStr: PropTypes.string,
    endTimeStr: PropTypes.string,
  }),
};

export default SurveySearchBox;
